import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Star, ExternalLink, Calendar } from 'lucide-react';
import { REVIEWS_DATA, HERO_DATA } from '../data/mockData';
import SEO from '../components/SEO';

const Reviews = () => {
  return (
    <div className="min-h-screen bg-[#111216]">
      <SEO 
        title="Detailing Disciples Reviews | Simi Valley Detailing"
        description="Read verified client feedback for Detailing Disciples and see the real vehicle transformations behind the reviews."
        canonical="https://thedetailingdisciples.com/reviews"
      />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-[#1A1A1F] to-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white">
              Reviews From <span className="text-[#B8860B]">Detailing Disciples</span> Clients
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              The best proof is what owners say after they see their vehicle. Read verified feedback on Google and Yelp, then browse the actual work behind the reviews.
            </p>

            {/* Overall Rating */}
            <div className="flex items-center justify-center gap-3 pt-6">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={`reviews-hero-star-${i}`} className="w-8 h-8 fill-[#B8860B] text-[#B8860B]" />
                ))}
              </div>
              <span className="text-white font-bold text-3xl">{HERO_DATA.rating}</span>
              <span className="text-gray-400 text-lg">({HERO_DATA.reviewCount} reviews on Yelp)</span>
            </div>

            {/* Review Platform Links */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-6">
              <a
                href="https://www.yelp.com/biz/detailing-disciples-simi-valley-2"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto"
              >
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-[#B8860B] hover:bg-[#C8960B] text-[#111216] font-bold"
                >
                  Read Yelp Reviews
                  <ExternalLink className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <a
                href="https://g.page/r/CbkD6ZBDY-FpEBM/review"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto"
              >
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto border-2 border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-[#111216] font-semibold"
                >
                  Read Google Reviews
                  <ExternalLink className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Verified Reviews Grid */}
      <section className="py-16 bg-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {REVIEWS_DATA.map((review) => (
                <div
                  key={review.id}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-[#B8860B]/50 transition-all"
                >
                  {/* Rating Stars */}
                  <div className="flex items-center mb-4">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={`review-${review.id}-star-${i}`} className="w-5 h-5 fill-[#B8860B] text-[#B8860B]" />
                    ))}
                  </div>

                  {/* Review Text */}
                  <p className="text-gray-300 leading-relaxed mb-6">
                    &ldquo;{review.text}&rdquo;
                  </p>

                  {/* Reviewer Info */}
                  <div className="border-t border-white/10 pt-4 space-y-2">
                    <p className="text-white font-bold text-lg">{review.name}</p>
                    <p className="text-[#B8860B] text-sm font-medium">{review.location}</p>
                    {review.vehicle && (
                      <p className="text-gray-500 text-sm">Vehicle: {review.vehicle}</p>
                    )}
                    {review.service && (
                      <p className="text-gray-500 text-sm">Service: {review.service}</p>
                    )}
                    <p className="text-gray-600 text-xs">{review.date}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* See Results CTA */}
      <section className="py-16 bg-gradient-to-b from-[#111216] to-[#1A1A1F]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            See the Work Behind the Reviews
          </h2>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            Browse before & after photos of actual paint correction, ceramic coating, and detailing projects.
          </p>
          <Link to="/results">
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-[#111216] font-semibold"
            >
              See Before & After Results
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-r from-[#B8860B] to-[#C8960B]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#111216] mb-4">
            Experience the Detailing Disciples Difference
          </h2>
          <p className="text-[#111216]/80 text-lg mb-8 max-w-2xl mx-auto">
            Owner-operated care with paint-safe methods and personalized recommendations.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-[#111216] hover:bg-[#1A1A1F] text-white font-bold"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Request Your Personalized Quote
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
